import { Link } from 'react-router-dom';
import type { NewsArticle } from '../../../mocks/news';
import { categoryBadgeClass } from '../../../services/news';
import { useTranslation } from 'react-i18next';

interface NewsFeaturedProps {
  article: NewsArticle;
}

export default function NewsFeatured({ article }: NewsFeaturedProps) {
  const { t } = useTranslation();
  const badgeClass = categoryBadgeClass(article.category);

  return (
    <section className="bg-white py-12">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-1 h-4 rounded-full bg-primary" />
          <span className="text-xs text-gray-400 uppercase tracking-widest font-medium">{t('newsPage.featured.title')}</span>
        </div>

        <Link
          to={`/news/${article.id}`}
          className="group relative grid grid-cols-1 lg:grid-cols-2 rounded-2xl overflow-hidden border border-primary/10 bg-light-alt hover:border-primary/25 transition-all duration-300 cursor-pointer"
        >
          <div className="relative h-64 lg:h-auto min-h-[320px] overflow-hidden">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-light-alt/80 hidden lg:block" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-light-alt/90 lg:hidden" />
            <div className="absolute top-5 left-5">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-primary/15 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-glow-pulse" />
                <span className="text-accent text-xs font-medium">{t('newsPage.featured.badge')}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-center p-8 lg:p-12">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className={`px-3 py-1 text-xs font-medium rounded-full border ${badgeClass}`}>{article.categoryLabel}</span>
              <div className="flex items-center gap-1.5 text-gray-400 text-xs">
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-calendar-line text-xs" /></div>
                {article.date}
              </div>
              <div className="flex items-center gap-1.5 text-gray-400 text-xs">
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line text-xs" /></div>
                {article.readTime}{t('newsPage.readSuffix')}
              </div>
            </div>

            <h2 className="text-2xl lg:text-3xl font-heading font-bold text-gray-900 leading-snug mb-4 group-hover:text-primary transition-colors duration-200">
              {article.title}
            </h2>

            <p className="text-gray-500 text-base leading-relaxed mb-6 line-clamp-3">{article.summary}</p>

            <div className="flex flex-wrap gap-2 mb-6">
              {article.tags.slice(0, 4).map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-white border border-primary/10 text-gray-400 text-xs rounded-md">{tag}</span>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                  <i className="ri-user-line text-primary text-sm" />
                </div>
                <div>
                  <div className="text-sm text-gray-700 font-medium">{article.author}</div>
                  <div className="text-xs text-gray-400">{article.authorRole}</div>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 text-primary text-sm font-medium group-hover:gap-3 transition-all duration-200">
                {t('newsPage.readFull')}
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}
